/** Small outcome dialogs for the per-type Calendar / Agenda row actions (Legal plan item 5). */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAgeingHandlers, performLegalEventAction, sortNames } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || e.message; };

// Field spec per action: [key, label, type, required?, extra]
const SPECS = {
  cancel:            { title: "Cancel this action", fields: [["reason", "Why is it being cancelled?", "textarea", true]], cta: "Cancel action", tone: "slate" },
  chase_again:       { title: "Chase again", fields: [["days", "Chase again in (days)", "number", true, { min: 1, max: 60, def: 7 }], ["reason", "Why — what did they say?", "textarea", true]], cta: "Push the chase" },
  response_received: { title: "Response received", fields: [["notes", "What came in? (letter, payment plan, dispute…)", "textarea", true]], cta: "Record response" },
  served:            { title: "Sent / served", fields: [["served_on", "Date sent / served", "date", false, { def: today() }], ["method", "Method (post, email, process server…)", "text", false]], cta: "Confirm served" },
  payment_received:  { title: "Payment received", fields: [["amount", "Amount (£, optional)", "text", false]], cta: "Record payment" },
  done:              { title: "Mark done", fields: [["notes", "Anything worth noting? (optional)", "textarea", false]], cta: "Done" },
  filed_ch:          { title: "Filed at Companies House", fields: [["filed_on", "Filed on", "date", false, { def: today() }], ["charge_code", "Charge code (optional)", "text", false]], cta: "Mark filed" },
  note:              { title: "Add a note to the file", fields: [["text", "Note", "textarea", true]], cta: "Add note" },
  passed_no_response:{ title: "Passed — no response", fields: [], cta: "Confirm — nothing came in", tone: "amber" },
  expired_no_payment:{ title: "Expired — no payment", fields: [], cta: "Confirm — no payment", tone: "amber" },
};

export function ActionDialog({ ev, action, onClose, onDone }) {
  const spec = SPECS[action];
  const [form, setForm] = useState(() => Object.fromEntries((spec?.fields || []).map(([k, , , , x]) => [k, x?.def ?? ""])));
  const [busy, setBusy] = useState(false);
  const auth = useAuth() || {};
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const ok = (spec?.fields || []).every(([k, , , req]) => !req || String(form[k] ?? "").trim().length > 0);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await performLegalEventAction(ev.event_id, action, form, auth.user?.name || "Ops", auth.isAdmin?.() || false);
      toast.success(res.next_hint ? `${spec.title} — ${res.next_hint}` : spec.title);
      await onDone?.(res);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!spec) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`lcal-action-dialog-${action}`}>
        <DialogHeader><DialogTitle className="text-sm">{spec.title} — {ev.label}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 -mt-1">{ev.customer_name || "—"} · {ev.loan_application_id || ""}</div>
        <div className="space-y-2">
          {spec.fields.map(([k, label, type, req, x]) => (
            <label key={k} className="block text-xs">
              <span className="text-slate-600">{label}{req ? " *" : ""}</span>
              {type === "textarea"
                ? <Textarea className="mt-1 text-xs min-h-[64px]" value={form[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`lcal-action-field-${k}`} />
                : <Input className="mt-1 h-8 text-xs" type={type} min={x?.min} max={x?.max} value={form[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`lcal-action-field-${k}`} />}
            </label>
          ))}
          {!spec.fields.length && <div className="text-xs text-slate-700">This closes the row and writes one line on the file timeline. The next step then shows as due on the Legal map.</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Back</Button>
          <Button size="sm" onClick={submit} disabled={!ok || busy} data-testid="lcal-action-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}{spec.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OUTCOMES = [
  ["adjourned", "Adjourned", "border-amber-500 bg-amber-50 text-amber-900"],
  ["order_made", "Order made", "border-slate-800 bg-slate-800 text-white"],
  ["dismissed", "Dismissed", "border-rose-500 bg-rose-50 text-rose-900"],
  ["struck_out", "Struck out", "border-rose-400 bg-rose-50 text-rose-800"],
  ["settled", "Settled at the door", "border-emerald-500 bg-emerald-50 text-emerald-900"],
];

/** Generic hearing outcome (non-petition hearings: charging orders, winding-up, other listings). */
export function GenericHearingOutcomeDialog({ ev, onClose, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [form, setForm] = useState({ new_date: "", reason: "", court_ref: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const auth = useAuth() || {};
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ok = outcome && (outcome !== "adjourned" || form.reason.trim().length >= 3);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await performLegalEventAction(ev.event_id, "record_outcome", { outcome, ...form }, auth.user?.name || "Ops", auth.isAdmin?.() || false);
      toast.success(res.new_date ? `Adjourned — re-listed ${res.new_date}` : `Hearing outcome recorded`);
      await onDone?.(res);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="lcal-hearing-outcome-dialog">
        <DialogHeader><DialogTitle className="text-sm">Record outcome — {ev.label}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 -mt-1">{ev.customer_name || "—"} · {ev.loan_application_id || ""}</div>
        <div className="grid grid-cols-2 gap-1.5">
          {OUTCOMES.map(([k, label, tone]) => (
            <button key={k} type="button" onClick={() => setOutcome(k)} data-testid={`lcal-hearing-outcome-${k}`}
              className={`text-xs px-2 py-1.5 rounded border text-left ${outcome === k ? tone : "border-slate-200 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </div>
        {outcome === "adjourned" && (
          <div className="space-y-2">
            <label className="block text-xs"><span className="text-slate-600">Re-listed date (leave blank if to be fixed — a chase to the court is diarised for 14 days)</span>
              <Input type="date" className="mt-1 h-8 text-xs" value={form.new_date} onChange={set("new_date")} data-testid="lcal-hearing-new-date" /></label>
            <label className="block text-xs"><span className="text-slate-600">Why adjourned? *</span>
              <Textarea className="mt-1 text-xs min-h-[56px]" value={form.reason} onChange={set("reason")} data-testid="lcal-hearing-reason" /></label>
          </div>
        )}
        {outcome && outcome !== "adjourned" && (
          <div className="space-y-2">
            <label className="block text-xs"><span className="text-slate-600">Court reference / order number (optional)</span>
              <Input className="mt-1 h-8 text-xs" value={form.court_ref} onChange={set("court_ref")} data-testid="lcal-hearing-court-ref" /></label>
            <label className="block text-xs"><span className="text-slate-600">Notes (optional)</span>
              <Textarea className="mt-1 text-xs min-h-[56px]" value={form.notes} onChange={set("notes")} data-testid="lcal-hearing-notes" /></label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Back</Button>
          <Button size="sm" onClick={submit} disabled={!ok || busy} data-testid="lcal-hearing-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Record outcome</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Allocate to…" — sets the legal action owner on the case; the Allocate row closes itself. */
export function AllocateDialog({ ev, onClose, onDone }) {
  const [handlers, setHandlers] = useState([]);
  const [who, setWho] = useState("");
  const [busy, setBusy] = useState(false);
  const auth = useAuth() || {};
  useEffect(() => { getAgeingHandlers("legal").then((h) => setHandlers(sortNames(h.handlers || []))).catch(() => setHandlers([])); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await performLegalEventAction(ev.event_id, "allocate", { handler: who }, auth.user?.name || "Ops", auth.isAdmin?.() || false);
      toast.success(`Allocated to ${res.handler || who}`);
      await onDone?.(res);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="lcal-allocate-dialog">
        <DialogHeader><DialogTitle className="text-sm">Allocate case handler — {ev.customer_name || "—"}</DialogTitle></DialogHeader>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger className="h-8 text-xs" data-testid="lcal-allocate-select"><SelectValue placeholder="Who takes the file?" /></SelectTrigger>
          <SelectContent>{handlers.map((h) => <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Back</Button>
          <Button size="sm" onClick={submit} disabled={!who || busy} data-testid="lcal-allocate-submit">{busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Allocate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
